import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import Hero from "../components/Hero";
import EditorialMarquee from "../components/Marquee";
import Services from "../components/Services";
import About from "../components/About";
import TrackRecord from "../components/TrackRecord";
import WorkGrid from "../components/WorkGrid";
import Testimonials from "../components/Testimonials";
import ContactFooter from "../components/ContactFooter";
import { Reveal } from "../components/Motion";
import { fetchCaseStudies } from "../lib/api";
import { scrollToSection } from "../lib/site";

function WorkPreview() {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["case-studies"],
        queryFn: fetchCaseStudies,
    });

    return (
        <section id="work" data-testid="work-section" className="border-t border-line px-5 md:px-10 py-24 md:py-32">
            <Reveal>
                <div className="mb-14 md:mb-20 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div>
                        <p className="eyebrow mb-6 flex items-center gap-3">
                            <span className="h-px w-10 bg-vermilion" /> Selected work
                        </p>
                        <h2 className="display-xl font-display font-light text-paper">
                            Proof, not promises<span className="text-vermilion">.</span>
                        </h2>
                    </div>
                    <div className="flex flex-col items-start gap-3 md:items-end">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-vermilion">
                            * The principal consultant's personal track record — end-to-end engagements
                        </p>
                        <Link
                            data-testid="all-work-link"
                            to="/work"
                            className="group flex items-center gap-3 border border-line px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:border-vermilion hover:text-vermilion"
                        >
                            All work
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </div>
            </Reveal>

            {isLoading && (
                <p className="py-20 font-mono text-xs uppercase tracking-[0.3em] text-smoke">
                    Loading the work…
                </p>
            )}
            {isError && (
                <p className="py-20 font-mono text-xs uppercase tracking-[0.3em] text-vermilion">
                    Couldn't load case studies — check the backend.
                </p>
            )}
            {data && <WorkGrid items={data.slice(0, 4)} />}
        </section>
    );
}

export default function Home() {
    const location = useLocation();

    useEffect(() => {
        const target = location.state?.scrollTo;
        if (target) {
            window.history.replaceState({}, "");
            setTimeout(() => scrollToSection(target), 500);
        }
    }, [location.state]);

    return (
        <main>
            <Hero />
            <EditorialMarquee />
            <Services />
            <About />
            <TrackRecord />
            <WorkPreview />
            <Testimonials />
            <ContactFooter />
        </main>
    );
}
